import type {
  GithubChoice,
  Item,
  ItemPriority,
  ItemView,
  SnoozePreset,
  TriageChoice,
} from '@hugo/core'
import { useMemo } from 'react'
import { askGithubDone, asksGithub } from '@/components/github-done-dialog'
import { triagedLabel } from './format'
import { useRunTool } from './items'
import { waitingDone } from './lists'
import { priorityLabel } from './priority'

export const GITHUB_DONE: Record<GithubChoice, string> = {
  testing: 'Done · moving to Testing in GitHub',
  close: 'Done · closing it in GitHub',
  hugo: 'Marked done',
}

const SNOOZE_DONE: Record<SnoozePreset, string> = {
  later_today: 'Snoozed for 3 hours',
  tomorrow: 'Snoozed until tomorrow',
  in_2_days: 'Snoozed for 2 days',
  next_week: 'Snoozed until next week',
}

/**
 * Where an item is listed now, for optimistic removal: open tasks, promises and waits are in the
 * one list (the `open` view).
 */
export function viewOf(item: Item): ItemView {
  if (item.status === 'inbox') return 'inbox'
  if (item.status === 'snoozed') return 'snoozed'
  if (item.status === 'done' || item.status === 'dropped') return 'done'
  return 'open'
}

/** Every item action the UI offers, as tool calls with optimistic UI and undo. */
export function useItemActions(via: 'ui' | 'palette' = 'ui') {
  const { run, undo } = useRunTool()
  return useMemo(() => {
    const away = (item: Item) => ({ view: viewOf(item), itemId: item.id })
    return {
      undo,
      triage: (item: Item, choice: TriageChoice) =>
        run(
          'item.triage',
          { id: item.id, choice },
          { done: triagedLabel(choice), optimistic: away(item), via },
        ),
      /** File an inbox item where hugo suggests, with the date the ask states (one undo). */
      accept: async (item: Item) => {
        const suggestion = item.suggestion
        if (!suggestion || item.status !== 'inbox') return null
        const { choice, dueDate, type, priority } = suggestion
        return run(
          'item.triage',
          {
            id: item.id,
            choice,
            ...(dueDate ? { due: { date: dueDate } } : {}),
            ...(type ? { type } : {}),
            // A high or low priority comes along as the user's own; normal leaves it to their rules.
            ...(priority !== 'normal' ? { priority } : {}),
          },
          { done: `${triagedLabel(choice)} (hugo's suggestion)`, optimistic: away(item), via },
        )
      },
      /** High, normal or low, the user's own; null: their priority rules decide again (one undo). */
      setPriority: (item: Item, priority: ItemPriority | null) =>
        run(
          'item.update',
          { id: item.id, priority },
          {
            done: priority ? `Priority: ${priorityLabel(priority)}` : 'Priority: your rules decide',
            optimistic: {
              view: viewOf(item),
              itemId: item.id,
              patch: priority
                ? { priority, prioritySet: true, priorityRuleIndex: null }
                : { prioritySet: false },
            },
            via,
          },
        ),
      /** Drop hugo's suggestion; the item stays where it is. Not undoable (the API's call). */
      dismissSuggestion: async (item: Item) => {
        if (!item.suggestion) return null
        return run(
          'item.dismiss_suggestion',
          { id: item.id },
          {
            done: 'Suggestion dismissed',
            optimistic: { view: viewOf(item), itemId: item.id, patch: { suggestion: null } },
            via,
          },
        )
      },
      move: (item: Item, kind: 'task' | 'owe' | 'waiting') =>
        item.status === 'inbox'
          ? run(
              'item.triage',
              { id: item.id, choice: kind },
              { done: triagedLabel(kind), optimistic: away(item), via },
            )
          : run(
              'item.set_kind',
              { id: item.id, kind },
              {
                done: waitingDone(kind),
                // It stays in the list (and on the board): only who waits changes.
                ...(item.kind === kind
                  ? {}
                  : { optimistic: { view: viewOf(item), itemId: item.id, patch: { kind } } }),
                via,
              },
            ),
      /** Done; a GitHub task first asks what GitHub should do (null: the user cancelled). */
      complete: async (item: Item) => {
        if (!asksGithub(item)) {
          return run(
            'item.complete',
            { id: item.id },
            { done: 'Marked done', optimistic: away(item), via },
          )
        }
        const github = await askGithubDone(item)
        if (github === null) return null
        return run(
          'item.complete',
          { id: item.id, github },
          { done: GITHUB_DONE[github], optimistic: away(item), via },
        )
      },
      drop: (item: Item) =>
        run('item.drop', { id: item.id }, { done: 'Dropped', optimistic: away(item), via }),
      reopen: (item: Item) =>
        run('item.reopen', { id: item.id }, { done: 'Reopened', optimistic: away(item), via }),
      snooze: (item: Item, preset: SnoozePreset) =>
        run(
          'item.snooze',
          { id: item.id, preset },
          { done: SNOOZE_DONE[preset], optimistic: away(item), via },
        ),
      snoozeUntil: (item: Item, until: string, label: string) =>
        run(
          'item.snooze',
          { id: item.id, until },
          { done: `Snoozed until ${label}`, optimistic: away(item), via },
        ),
    }
  }, [run, undo, via])
}
